import { apiFetch } from '../../../../utils/api';

// Family Selection Handlers
export const handleSelectFamily = (setSelectedFamilyId, setIsEditMode) => (familyId) => {
    setSelectedFamilyId(familyId);
    setIsEditMode(false);
};

export const handleEditFamily = (setSelectedFamilyId, setIsEditMode) => (e, familyId) => {
    e.stopPropagation();
    setSelectedFamilyId(familyId);
    setIsEditMode(true);
};

// Family CRUD Handlers
export const handleRenameFamily = (
    selectedFamilyId,
    editFamilyName,
    setFamilies,
    setIsEditingName,
    setSavingFamilyName,
    fetchData
) => async (e) => {
    if (e) e.preventDefault();
    if (!selectedFamilyId || !editFamilyName.trim()) return;

    setSavingFamilyName(true);
    try {
        const res = await apiFetch(`/api/products/${selectedFamilyId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ Name: editFamilyName.trim() })
        });

        if (res.ok) {
            // Update local list
            setFamilies(prev => prev.map(f => f.id === selectedFamilyId ? { ...f, name: editFamilyName.trim() } : f));
            setIsEditingName(false);
            // Trigger context changed event
            window.dispatchEvent(new Event('activeProjectChanged'));
        } else {
            alert(`Error renaming family: ${await res.text()}`);
        }
    } catch (err) {
        console.error('Failed to rename family', err);
    } finally {
        setSavingFamilyName(false);
    }
};

export const handleCreateFamily = async (
    e,
    newFamilyName,
    activeProjectId,
    setNewFamilyName,
    setIsCreatingFamily,
    fetchData,
    setSelectedFamilyId
) => {
    if (e) e.preventDefault();
    if (!newFamilyName.trim()) return;

    try {
        const res = await apiFetch(`/api/products`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ Name: newFamilyName.trim() })
        });

        if (res.ok) {
            const family = await res.json();
            
            // If active project is set, auto-link it
            if (activeProjectId) {
                await apiFetch(`/api/projects/${activeProjectId}/link-product`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ productFamilyId: family.id })
                });
            }

            setNewFamilyName('');
            setIsCreatingFamily(false);
            await fetchData();
            setSelectedFamilyId(family.id);
        } else {
            alert(`Error creating family: ${await res.text()}`);
        }
    } catch (err) {
        console.error('Failed to create family', err);
    }
};

export const handleDeleteFamilyClick = (setDeletingFamilyId, setIsDeletingFamily, setDeleteRuleSetsCheck) => (e, familyId) => {
    e.stopPropagation();
    setDeletingFamilyId(familyId);
    setIsDeletingFamily(true);
    setDeleteRuleSetsCheck(false);
};

export const handleDeleteFamilySubmit = (
    setSavingFamilyName,
    deletingFamilyId,
    families,
    deleteRuleSetsCheck,
    setIsDeletingFamily,
    setDeletingFamilyId,
    setDeleteRuleSetsCheck,
    fetchData,
    setSelectedFamilyId,
    setIsEditMode
) => async (e) => {
    if (e) e.preventDefault();
    if (!deletingFamilyId) return;
    setSavingFamilyName(true);
    try {
        const res = await apiFetch(`/api/products/${deletingFamilyId}?deleteRuleSets=${deleteRuleSetsCheck}`, {
            method: 'DELETE'
        });
        if (res.ok) {
            setIsDeletingFamily(false);
            setDeletingFamilyId(null);
            setDeleteRuleSetsCheck(false);
            setSelectedFamilyId(null);
            setIsEditMode(false);
            await fetchData();
        } else {
            alert(`Error deleting family: ${await res.text()}`);
        }
    } catch (err) {
        console.error(err);
    } finally {
        setSavingFamilyName(false);
    }
};

export const handleShareFamily = (fetchData, setShareFamily) => async (family, projectIds) => {
    try {
        for (const projectId of projectIds) {
            const res = await apiFetch(`/api/projects/${projectId}/link-product`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productFamilyId: family.id })
            });
            if (!res.ok) {
                alert(`Failed to share with a project: ${await res.text()}`);
                return;
            }
        }
        setShareFamily(null);
        await fetchData();
    } catch (error) {
        console.error('Failed to share family', error);
        alert(`Failed to share family: ${error.message}`);
    }
};

export const handleUnlinkFamilyFromProject = (fetchData) => async (family, project) => {
    if (!family?.id || !project?.id) return;
    if (!await window.confirm(`Remove '${family.name}' from project '${project.name}'?\nThe family itself is not deleted.`)) {
        return;
    }

    try {
        const res = await apiFetch(`/api/projects/${project.id}/unlink-product/${family.id}`, {
            method: 'DELETE'
        });
        if (!res.ok) {
            alert(`Failed to remove family: ${await res.text()}`);
            return;
        }
        await fetchData();
    } catch (error) {
        console.error('Failed to unlink family', error);
        alert(`Failed to remove family: ${error.message}`);
    }
};

export const handleUnshareFamilyFromProject = (fetchData) => async (family, project, shareInfo) => {
    if (!family?.id || !project?.id) return;
    const originalName = shareInfo?.originalProject?.name || 'the original project';
    if (!await window.confirm(`Unshare '${family.name}' from '${project.name}'?\nThis project will keep an independent copy. ${originalName} will keep the original.`)) {
        return;
    }

    try {
        const res = await apiFetch(`/api/projects/${project.id}/unshare-product`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ productFamilyId: family.id })
        });
        if (!res.ok) {
            alert(`Failed to unshare family: ${await res.text()}`);
            return;
        }
        await fetchData();
    } catch (error) {
        console.error('Failed to unshare family', error);
        alert(`Failed to unshare family: ${error.message}`);
    }
};

export const handleLinkExistingFamilies = (fetchData, setIsLinkFamilyOpen) => async (projectId, familyIds) => {
    try {
        for (const familyId of familyIds) {
            const res = await apiFetch(`/api/projects/${projectId}/link-product`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productFamilyId: familyId })
            });
            if (!res.ok) {
                alert(`Failed to link family: ${await res.text()}`);
                return;
            }
        }
        setIsLinkFamilyOpen(false);
        await fetchData();
    } catch (error) {
        console.error('Failed to link families', error);
        alert(`Failed to link family: ${error.message}`);
    }
};

export const handleExportFamily = async (familyId, familyName) => {
    if (!familyId) return;

    try {
        const res = await apiFetch(`/api/products/${familyId}/export`);
        if (res.ok) {
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${familyName.replace(/\s+/g, '_')}.product`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } else {
            alert(`Error exporting family: ${await res.text()}`);
        }
    } catch (err) {
        console.error('Failed to export family', err);
        alert(`Failed to export family: ${err.message}`);
    }
};

export const handleImportClick = (fileInputRef) => () => {
    fileInputRef.current?.click();
};

export const handleFileChange = async (
    e,
    activeProjectId,
    setLoading,
    fetchData,
    setSelectedFamilyId
) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
        setLoading(true);
        const res = await apiFetch(`/api/products/import`, {
            method: 'POST',
            body: formData
        });

        if (res.ok) {
            const data = await res.json();
            const newFamId = data.id || data.Id || data.ID;
            
            // If active project is set, auto-link it
            if (activeProjectId && newFamId) {
                await apiFetch(`/api/projects/${activeProjectId}/link-product`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ productFamilyId: newFamId })
                });
            }

            alert(`Family '${data.name}' imported successfully!`);
            await fetchData();
            setSelectedFamilyId(newFamId);
        } else {
            alert(`Error importing family: ${await res.text()}`);
        }
    } catch (err) {
        console.error('Failed to import family', err);
        alert(`Failed to import family: ${err.message}`);
    } finally {
        setLoading(false);
        e.target.value = ''; // Reset file input
    }
};
